import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, EnvelopeSimple, User, ShieldCheck, Eye, EyeSlash, FloppyDisk, BellRinging, ClockClockwise, SignIn, Key, ArrowsClockwise } from '@phosphor-icons/react';

const fmtDateTime = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return null; }
};

const ActivityRow = ({ icon: Icon, label, value, hint, testid }) => (
  <div data-testid={testid} className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-b-0">
    <Icon size={18} weight="duotone" className="text-[#C5A059] mt-0.5 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568]">{label}</p>
      <p className="text-sm text-[#0A1128] mt-0.5 font-medium">{value || <span className="text-[#A0AABF] font-normal">{hint}</span>}</p>
    </div>
  </div>
);

const PasswordField = ({ id, label, value, onChange, testid, autoComplete = 'new-password', placeholder = '••••••••' }) => {  const [show, setShow] = useState(false);
  return (
    <div>
      <Label htmlFor={id} className="text-sm font-medium text-[#1A202C] mb-2 block">{label}</Label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={18} />
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          data-testid={testid}
          autoComplete={autoComplete}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="pl-10 pr-10 border-slate-300 bg-white text-slate-900 rounded-md focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059]"
        />
        <button
          type="button"
          aria-label={show ? 'Hide password' : 'Show password'}
          data-testid={`${testid}-toggle`}
          onClick={() => setShow((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5568] hover:text-[#0A1128]"
        >
          {show ? <EyeSlash size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );
};

const Profile = () => {
  // Account info
  const [me, setMe] = useState(null);
  const [loadingMe, setLoadingMe] = useState(true);
  const [fullName, setFullName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Account activity
  const [activity, setActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(true);

  // Notification email
  const [notificationEmail, setNotificationEmail] = useState('');
  const [originalNotificationEmail, setOriginalNotificationEmail] = useState('');
  const [savingNotificationEmail, setSavingNotificationEmail] = useState(false);
  const [loadingPrefs, setLoadingPrefs] = useState(true);

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/auth/me')
      .then((res) => {
        if (cancelled) return;
        setMe(res.data);
        setFullName(res.data.full_name || '');
      })
      .catch(() => { if (!cancelled) toast.error('Could not load your account information'); })
      .finally(() => { if (!cancelled) setLoadingMe(false); });

    api.get('/notifications/preferences')
      .then((res) => {
        if (cancelled) return;
        const addr = res.data?.email_address || '';
        setNotificationEmail(addr);
        setOriginalNotificationEmail(addr);
      })
      .catch(() => { /* notification prefs might 404 if never set, that's fine */ })
      .finally(() => { if (!cancelled) setLoadingPrefs(false); });

    api.get('/auth/account-activity')
      .then((res) => { if (!cancelled) setActivity(res.data); })
      .catch(() => { /* keep activity null; UI shows neutral placeholders */ })
      .finally(() => { if (!cancelled) setLoadingActivity(false); });

    return () => { cancelled = true; };
  }, []);

  // ---- Display name handlers ----
  const trimmedName = fullName.trim();
  const nameChanged = me && trimmedName && trimmedName !== (me.full_name || '');
  const canSaveName = nameChanged && !savingName;

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!canSaveName) return;
    setSavingName(true);
    try {
      const { data } = await api.put('/auth/profile', { full_name: trimmedName });
      setMe(data);
      setFullName(data.full_name || '');
      // Push the fresh user payload into the global App state so the sidebar
      // reflects the new display name immediately.
      window.dispatchEvent(new CustomEvent('wn-user-updated', { detail: data }));
      toast.success('Display name updated');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not update your name. Please try again.');
    } finally {
      setSavingName(false);
    }
  };

  // ---- Notification email handlers ----
  const notifChanged = notificationEmail !== originalNotificationEmail;
  const validEmailOrEmpty = !notificationEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notificationEmail);
  const canSaveNotifEmail = notifChanged && validEmailOrEmpty && !savingNotificationEmail;

  const handleSaveNotificationEmail = async (e) => {
    e.preventDefault();
    if (!canSaveNotifEmail) return;
    setSavingNotificationEmail(true);
    try {
      await api.put('/notifications/preferences', { email_address: notificationEmail || null });
      setOriginalNotificationEmail(notificationEmail);
      toast.success(notificationEmail ? 'Notification email saved' : 'Notification email cleared — we\'ll use your login email');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save your notification email.');
    } finally {
      setSavingNotificationEmail(false);
    }
  };

  // ---- Password handlers ----
  const mismatch = newPassword && confirmPassword && newPassword !== confirmPassword;
  const tooShort = newPassword && newPassword.length < 8;
  const sameAsOld = currentPassword && newPassword && currentPassword === newPassword;
  const canSubmit = currentPassword && newPassword && confirmPassword && !mismatch && !tooShort && !sameAsOld && !savingPassword;

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSavingPassword(true);
    try {
      const { data } = await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success(data.message || 'Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not update your password. Please try again.');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="profile-page">
      <div>
        <h1 className="cabinet-grotesk text-3xl font-medium text-[#0A1128]">Profile &amp; Security</h1>
        <p className="text-[#4A5568] mt-1">Manage your account information, notification email, and password.</p>
      </div>

      {/* Account Activity */}
      <section className="bg-white border border-slate-200 rounded-md shadow-sm dashboard-card overflow-hidden" data-testid="account-activity-section">
        <header className="px-6 py-4 border-b border-slate-200 bg-[#FDFCFB] flex items-center gap-3">
          <ClockClockwise size={20} weight="duotone" className="text-[#C5A059]" />
          <h2 className="text-base font-semibold text-[#1A202C]">Account Activity</h2>
          <span className="ml-auto text-xs text-[#A0AABF]">Informational only — no device or location tracking.</span>
        </header>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-8">
          <ActivityRow
            icon={SignIn}
            label="Last login"
            value={loadingActivity ? '…' : fmtDateTime(activity?.last_login)}
            hint="This is your first session — welcome!"
            testid="activity-last-login"
          />
          <ActivityRow
            icon={ClockClockwise}
            label="Previous login"
            value={loadingActivity ? '…' : fmtDateTime(activity?.previous_login)}
            hint="No earlier session on record yet."
            testid="activity-previous-login"
          />
          <ActivityRow
            icon={Key}
            label="Last password change"
            value={loadingActivity ? '…' : fmtDateTime(activity?.last_password_change)}
            hint="You haven't changed your password yet."
            testid="activity-last-password-change"
          />
          <ActivityRow
            icon={ArrowsClockwise}
            label="Last password reset"
            value={loadingActivity ? '…' : fmtDateTime(activity?.last_password_reset)}
            hint="No reset has been completed."
            testid="activity-last-password-reset"
          />
        </div>
        <div className="px-6 pb-5 -mt-2">
          <p className="text-xs text-[#4A5568] leading-relaxed">
            See something unfamiliar? Change your password immediately using the section below.
          </p>
        </div>
      </section>

      {/* Account Information */}
      <section className="bg-white border border-slate-200 rounded-md shadow-sm dashboard-card overflow-hidden">
        <header className="px-6 py-4 border-b border-slate-200 bg-[#FDFCFB] flex items-center gap-3">
          <User size={20} weight="duotone" className="text-[#C5A059]" />
          <h2 className="text-base font-semibold text-[#1A202C]">Account Information</h2>
        </header>
        <form onSubmit={handleSaveName} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl" data-testid="display-name-form">
          <div>
            <Label htmlFor="full_name" className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2 block">Display Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={18} />
              <Input
                id="full_name"
                type="text"
                data-testid="display-name-input"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={loadingMe}
                placeholder="Your name"
                className="pl-10 border-slate-300 bg-white text-slate-900 rounded-md focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059]"
                maxLength={120}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2 block">Email (sign-in)</Label>
            <p data-testid="profile-email" className="flex items-center gap-2 px-3 py-2 bg-[#FDFCFB] border border-slate-200 rounded-md text-sm text-[#1A202C]">
              <EnvelopeSimple size={16} className="text-[#4A5568]" />
              {loadingMe ? '…' : (me?.email || '—')}
            </p>
            <p className="text-xs text-[#A0AABF] mt-1">Used to sign in. Contact support to change.</p>
          </div>
          <div className="md:col-span-2 flex items-center gap-3">
            <Button
              type="submit"
              data-testid="save-display-name-button"
              disabled={!canSaveName}
              className="bg-[#0A1128] text-white hover:bg-[#152145] rounded-md py-2.5 px-5 text-sm font-medium transition-colors flex items-center gap-2"
            >
              <FloppyDisk size={16} />
              {savingName ? 'Saving…' : 'Save name'}
            </Button>
            {nameChanged && <span className="text-xs text-[#C5A059]">Unsaved changes</span>}
          </div>
        </form>
      </section>

      {/* Notification Email */}
      <section className="bg-white border border-slate-200 rounded-md shadow-sm dashboard-card overflow-hidden">
        <header className="px-6 py-4 border-b border-slate-200 bg-[#FDFCFB] flex items-center gap-3">
          <BellRinging size={20} weight="duotone" className="text-[#C5A059]" />
          <h2 className="text-base font-semibold text-[#1A202C]">Notification Email</h2>
        </header>
        <form onSubmit={handleSaveNotificationEmail} className="p-6 space-y-5 max-w-xl" data-testid="notification-email-form">
          <div>
            <Label htmlFor="notification_email" className="text-sm font-medium text-[#1A202C] mb-2 block">Where should we send milestone alerts &amp; weekly digests?</Label>
            <div className="relative">
              <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={18} />
              <Input
                id="notification_email"
                type="email"
                data-testid="notification-email-input"
                placeholder={me?.email ? `Leave blank to use ${me.email}` : 'name@example.com'}
                value={notificationEmail}
                onChange={(e) => setNotificationEmail(e.target.value)}
                disabled={loadingPrefs}
                className="pl-10 border-slate-300 bg-white text-slate-900 rounded-md focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059]"
              />
            </div>
            <p className="text-xs text-[#A0AABF] mt-2">
              Leave blank to fall back to your sign-in email. This does not change your login.
            </p>
            {!validEmailOrEmpty && (
              <p data-testid="notification-email-invalid" className="text-xs text-[#C53030] mt-2">Please enter a valid email address.</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="submit"
              data-testid="save-notification-email-button"
              disabled={!canSaveNotifEmail}
              className="bg-[#0A1128] text-white hover:bg-[#152145] rounded-md py-2.5 px-5 text-sm font-medium transition-colors flex items-center gap-2"
            >
              <FloppyDisk size={16} />
              {savingNotificationEmail ? 'Saving…' : 'Save notification email'}
            </Button>
            {notifChanged && <span className="text-xs text-[#C5A059]">Unsaved changes</span>}
          </div>
        </form>
      </section>

      {/* Change Password */}
      <section className="bg-white border border-slate-200 rounded-md shadow-sm dashboard-card overflow-hidden">
        <header className="px-6 py-4 border-b border-slate-200 bg-[#FDFCFB] flex items-center gap-3">
          <ShieldCheck size={20} weight="duotone" className="text-[#C5A059]" />
          <h2 className="text-base font-semibold text-[#1A202C]">Change Password</h2>
        </header>
        <form onSubmit={handleChangePassword} className="p-6 space-y-5 max-w-xl" data-testid="change-password-form">
          <PasswordField
            id="current_password"
            label="Current password"
            value={currentPassword}
            onChange={setCurrentPassword}
            testid="current-password-input"
            autoComplete="current-password"
          />
          <PasswordField
            id="new_password"
            label="New password (8+ characters)"
            value={newPassword}
            onChange={setNewPassword}
            testid="new-password-input"
          />
          <PasswordField
            id="confirm_password"
            label="Confirm new password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            testid="confirm-password-input"
          />

          {tooShort && (
            <p data-testid="profile-validation-too-short" className="text-xs text-[#C53030]">Password must be at least 8 characters.</p>
          )}
          {mismatch && (
            <p data-testid="profile-validation-mismatch" className="text-xs text-[#C53030]">New password and confirmation do not match.</p>
          )}
          {sameAsOld && (
            <p data-testid="profile-validation-same" className="text-xs text-[#C53030]">New password must differ from your current password.</p>
          )}

          <div className="flex items-center gap-3 pt-2">
            <Button
              type="submit"
              data-testid="change-password-submit"
              disabled={!canSubmit}
              className="bg-[#0A1128] text-white hover:bg-[#152145] rounded-md py-2.5 px-6 text-sm font-medium transition-colors"
            >
              {savingPassword ? 'Updating…' : 'Update password'}
            </Button>
            <p className="text-xs text-[#A0AABF]">You'll stay signed in after the change. Other reset links will be invalidated.</p>
          </div>
        </form>
      </section>
    </div>
  );
};

export default Profile;
